import React, { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { InstallationFormModel } from 'src/Libs/flow/installation/forms'

const useExpandCollapseState = (
  storageDevices: InstallationFormModel['storageDevices']
): {
  expandState: Record<string, boolean>
  areAllCollapsed: boolean
  areAllExpanded: boolean
  handleCollapseAll: () => void
  handleExpandAll: () => void
  handleToggleExpand: (deviceId: string) => () => void
} => {
  const { installationId } = useParams<{ installationId: string }>()

  const initialExpandState: Record<string, boolean> = (() => {
    const expandState = Object.fromEntries(storageDevices.map(storage => [storage.deviceId, false]))
    const savedExpandState = localStorage.getItem('ADVANCED_SETTINGS_STORAGE_TAB_EXPAND_STATE')

    try {
      const savedExpandStateForInstallation: Record<string, boolean> | null =
        installationId && savedExpandState ? JSON.parse(savedExpandState)[installationId] : null

      return savedExpandStateForInstallation || expandState
    } catch {
      return expandState
    }
  })()
  const [expandState, setExpandState] = React.useState<Record<string, boolean>>(initialExpandState)

  useRemoveUnavailableDevicesFromLocalStorage(storageDevices, setExpandState)

  const areAllCollapsed = Object.values(expandState).every(expanded => expanded === false)
  const areAllExpanded = Object.values(expandState).every(expanded => expanded === true)

  const updateLocalStorage = (newExpandState: Record<string, boolean>): void => {
    const savedExpandState = localStorage.getItem('ADVANCED_SETTINGS_STORAGE_TAB_EXPAND_STATE')

    try {
      if (!installationId) return
      const parsedLocalStorageState = savedExpandState ? JSON.parse(savedExpandState) : {}
      const savedExpandStateForInstallation: Record<string, boolean> = parsedLocalStorageState[installationId]

      const updatedExpandStateForInstallation = { ...savedExpandStateForInstallation, ...newExpandState }
      const updatedLocalStorageState = {
        ...parsedLocalStorageState,
        [installationId]: updatedExpandStateForInstallation,
      }
      localStorage.setItem('ADVANCED_SETTINGS_STORAGE_TAB_EXPAND_STATE', JSON.stringify(updatedLocalStorageState))
    } catch {
      console.error('Failed to update saved expand state in localStorage')
    }
  }
  const handleCollapseAll = (): void => {
    const newExpandState = Object.fromEntries(storageDevices.map(storage => [storage.deviceId, false]))
    setExpandState(newExpandState)
    updateLocalStorage(newExpandState)
  }
  const handleExpandAll = (): void => {
    const newExpandState = Object.fromEntries(storageDevices.map(storage => [storage.deviceId, true]))
    setExpandState(newExpandState)
    updateLocalStorage(newExpandState)
  }
  const handleToggleExpand = (deviceId: string) => (): void => {
    const newExpandState = { ...expandState, [deviceId]: !expandState[deviceId] }
    setExpandState(newExpandState)
    updateLocalStorage(newExpandState)
  }

  return { expandState, areAllCollapsed, areAllExpanded, handleCollapseAll, handleExpandAll, handleToggleExpand }
}

export default useExpandCollapseState

const useRemoveUnavailableDevicesFromLocalStorage = (
  storageDevices: InstallationFormModel['storageDevices'],
  setExpandState: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
): void => {
  const { installationId } = useParams<{ installationId: string }>()

  useEffect(() => {
    const savedExpandState = localStorage.getItem('ADVANCED_SETTINGS_STORAGE_TAB_EXPAND_STATE')
    if (!savedExpandState || !installationId) return

    try {
      const parsedLocalStorageState = JSON.parse(savedExpandState)
      const savedExpandStateForInstallation: Record<string, boolean> | null =
        installationId && parsedLocalStorageState ? parsedLocalStorageState[installationId] : null

      if (!savedExpandStateForInstallation) return

      const currentStorageDeviceIds = storageDevices.map(storage => storage.deviceId)
      const hasStaleKey = Object.keys(savedExpandStateForInstallation).some(deviceId => !currentStorageDeviceIds.includes(deviceId))

      if (hasStaleKey) {
        const newSavedExpandStateForInstallation = Object.fromEntries(
          Object.entries(savedExpandStateForInstallation).filter(([deviceId]) => currentStorageDeviceIds.includes(deviceId))
        )

        const updatedLocalStorageState = {
          ...parsedLocalStorageState,
          [installationId]: newSavedExpandStateForInstallation,
        }

        setExpandState(newSavedExpandStateForInstallation)
        localStorage.setItem('ADVANCED_SETTINGS_STORAGE_TAB_EXPAND_STATE', JSON.stringify(updatedLocalStorageState))
      }
    } catch {
      console.error('Failed to remove not available devices from saved expand state in localStorage')
    }
  }, [storageDevices, installationId, setExpandState])
}
